import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Chip,
  Avatar,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import InventoryIcon from '@mui/icons-material/Inventory';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import StripeConnectSection from '../../components/StripeConnectSection';

const StorePartner = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [isOpen, setIsOpen] = useState(true);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState({});
  const [productDialog, setProductDialog] = useState(false);
  const [currentProduct, setCurrentProduct] = useState({
    name: '',
    category: '',
    price: '',
    stock: '',
  });

  useEffect(() => {
    loadStoreData();
  }, []);

  const loadStoreData = async () => {
    try {
      const [statsRes, ordersRes, productsRes] = await Promise.all([
        api.get('/stores/partner/stats'),
        api.get('/stores/partner/orders'),
        api.get('/stores/partner/products'),
      ]);
      setStats(statsRes.data.stats || {});
      setOrders(ordersRes.data.orders || []);
      setProducts(productsRes.data.products || []);
    } catch (err) {
      setStats({});
      setOrders([]);
      setProducts([]);
    }
  };

  const handleToggleOpen = () => {
    setIsOpen(!isOpen);
  };

  const handleAcceptOrder = (orderId) => {
    setOrders(orders.map(order =>
      order.id === orderId ? { ...order, status: 'preparing' } : order
    ));
  };

  const handleOrderReady = (orderId) => {
    setOrders(orders.map(order =>
      order.id === orderId ? { ...order, status: 'ready' } : order
    ));
  };

  const handleSaveProduct = () => {
    if (currentProduct.id) {
      setProducts(products.map(p => p.id === currentProduct.id ? currentProduct : p));
    } else {
      setProducts([...products, { ...currentProduct, id: Date.now() }]);
    }
    setProductDialog(false);
    resetForm();
  };

  const openProductDialog = (product = null) => {
    if (product) {
      setCurrentProduct(product);
    } else {
      resetForm();
    }
    setProductDialog(true);
  };

  const resetForm = () => {
    setCurrentProduct({ name: '', category: '', price: '', stock: '' });
  };

  const getStatusColor = (status) => {
    const colors = { pending: 'warning', preparing: 'info', ready: 'success', delivered: 'default' };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = { pending: 'En attente', preparing: 'En préparation', ready: 'Prêt', delivered: 'Livré' };
    return labels[status] || status;
  };

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', pb: 4 }}>
      <Box sx={{ bgcolor: 'success.main', color: 'white', py: 4 }}>
        <Container maxWidth="xl">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ width: 60, height: 60, bgcolor: 'white', color: 'success.main' }}>
                <StorefrontIcon fontSize="large" />
              </Avatar>
              <Box>
                <Typography variant="h4" fontWeight={700}>Supermarché Kin</Typography>
                <Typography variant="body2">Magasin partenaire</Typography>
              </Box>
            </Box>
            <FormControlLabel
              control={<Switch checked={isOpen} onChange={handleToggleOpen} />}
              label={isOpen ? 'Ouvert' : 'Fermé'}
              sx={{ color: 'white' }}
            />
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ mt: -4 }}>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ bgcolor: 'info.light', width: 56, height: 56 }}>
                    <ShoppingCartIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={700}>{stats.today_orders}</Typography>
                    <Typography variant="body2" color="text.secondary">Commandes aujourd'hui</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ bgcolor: 'success.light', width: 56, height: 56 }}>
                    <AttachMoneyIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={700}>{stats.today_revenue} €</Typography>
                    <Typography variant="body2" color="text.secondary">CA du jour</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ bgcolor: 'warning.light', width: 56, height: 56 }}>
                    <CheckCircleIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={700}>{stats.pending_orders}</Typography>
                    <Typography variant="body2" color="text.secondary">En attente</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ bgcolor: 'info.light', width: 56, height: 56 }}>
                    <InventoryIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={700}>{stats.total_products}</Typography>
                    <Typography variant="body2" color="text.secondary">Produits</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {orders.filter(o => o.status === 'pending').length > 0 && (
          <Alert severity="warning" icon={<NotificationsActiveIcon />} sx={{ mb: 3 }}>
            Vous avez <strong>{orders.filter(o => o.status === 'pending').length}</strong> nouvelle(s) commande(s) en attente !
          </Alert>
        )}

        <Paper sx={{ mb: 3 }}>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
            <Tab label="Commandes" />
            <Tab label="Inventaire" />
            <Tab label="Statistiques" />
            <Tab label="Paiements" />
          </Tabs>
        </Paper>

        {tabValue === 0 && (
          <Grid container spacing={3}>
            {orders.map((order) => (
              <Grid item xs={12} key={order.id}>
                <Card>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                      <Box>
                        <Typography variant="h6" fontWeight={600}>Commande #{order.order_number}</Typography>
                        <Typography variant="body2" color="text.secondary">{order.created_at}</Typography>
                      </Box>
                      <Chip label={getStatusLabel(order.status)} color={getStatusColor(order.status)} />
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" fontWeight={600}>Client</Typography>
                    <Typography variant="body2">{order.customer}</Typography>
                    <Typography variant="body2" color="text.secondary">{order.phone}</Typography>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" fontWeight={600}>Liste de courses</Typography>
                      <Typography variant="body2" color="text.secondary">{order.items_list}</Typography>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="h6" fontWeight={700}>Total</Typography>
                      <Typography variant="h6" color="primary" fontWeight={700}>{order.total} €</Typography>
                    </Box>
                    <Box display="flex" gap={2} mt={3}>
                      {order.status === 'pending' && (
                        <Button variant="contained" color="success" fullWidth onClick={() => handleAcceptOrder(order.id)}>
                          Accepter et préparer
                        </Button>
                      )}
                      {order.status === 'preparing' && (
                        <Button variant="contained" color="success" fullWidth onClick={() => handleOrderReady(order.id)}>
                          Marquer comme prêt
                        </Button>
                      )}
                      {order.status === 'ready' && (
                        <Chip label="En attente du livreur" color="success" sx={{ width: '100%', height: 40 }} />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {tabValue === 1 && (
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h5" fontWeight={700}>Gestion de l'inventaire</Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => openProductDialog()}>
                Ajouter un produit
              </Button>
            </Box>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell><strong>Produit</strong></TableCell>
                    <TableCell><strong>Catégorie</strong></TableCell>
                    <TableCell align="right"><strong>Prix (€)</strong></TableCell>
                    <TableCell align="right"><strong>Stock</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {products.map((product) => (
                    <TableRow key={product.id}>
                      <TableCell>{product.name}</TableCell>
                      <TableCell>{product.category}</TableCell>
                      <TableCell align="right">{product.price} €</TableCell>
                      <TableCell align="right">
                        <Chip label={product.stock} color={product.stock < 20 ? 'error' : 'success'} size="small" />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => openProductDialog(product)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {tabValue === 2 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Statistiques détaillées</Typography>
              <Button variant="contained" onClick={() => navigate('/partner/dashboard')} sx={{ mt: 2 }}>
                Voir le tableau de bord complet
              </Button>
            </CardContent>
          </Card>
        )}

        {tabValue === 3 && (
          <StripeConnectSection commissionRate={10} commissionLabel="sur le montant des commandes" />
        )}
      </Container>

      <Dialog open={productDialog} onClose={() => setProductDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{currentProduct.id ? 'Modifier' : 'Ajouter'} un produit</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Nom du produit" value={currentProduct.name}
                onChange={(e) => setCurrentProduct({ ...currentProduct, name: e.target.value })} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Catégorie" value={currentProduct.category}
                onChange={(e) => setCurrentProduct({ ...currentProduct, category: e.target.value })} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Prix (€)" type="number" value={currentProduct.price}
                onChange={(e) => setCurrentProduct({ ...currentProduct, price: e.target.value })} required />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Stock" type="number" value={currentProduct.stock}
                onChange={(e) => setCurrentProduct({ ...currentProduct, stock: e.target.value })} required />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProductDialog(false)}>Annuler</Button>
          <Button onClick={handleSaveProduct} variant="contained">Enregistrer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StorePartner;
